import React from "react";
import BoxInputs from "./BoxInputs";

export default function RateCalculatorForm({
  pickupPincode,
  setPickupPincode,
  deliveryPincode,
  setDeliveryPincode,
  packageType,
  setPackageType,
  showAdvanced,
  setShowAdvanced,
  boxes,
  errors,
  paymentMode,
  setPaymentMode,
  codAmount,
  setCodAmount,
  insuranceOption,
  setInsuranceOption,
  invoiceValue,
  setInvoiceValue,
  selfDropOption,
  setSelfDropOption,
  handleBoxChange,
  addBox,
  deleteBox,
  onCalculate, // <-- Add this prop
}) {
  const isB2B = packageType === "B2B";

  // Function to prevent negative values
  const preventNegative = (e) => {
    if (e.key === '-' || e.key === 'e') {
      e.preventDefault();
    }
  };

  return (
    <div>
      <form className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left Column */}
          <div className="space-y-8">
            {/* Pincode Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Pickup Pincode
                </label>
                <input
                  type="number"
                  min="0"
                  value={pickupPincode}
                  onChange={(e) => setPickupPincode(e.target.value)}
                  onKeyDown={preventNegative}
                  className={`w-full border rounded-xl px-5 py-3 text-gray-800 placeholder-indigo-300 focus:outline-none focus:ring-4 transition ${
                    errors.pickupPincode
                      ? "border-red-500 focus:ring-red-300"
                      : "border-indigo-300 focus:ring-indigo-300"
                  }`}
                  placeholder="Enter pickup pincode"
                />
                {errors.pickupPincode && (
                  <p className="text-blue-500 text-sm mt-1">
                    {errors.pickupPincode}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Delivery Pincode
                </label>
                <input
                  type="number"
                  min="0"
                  value={deliveryPincode}
                  onChange={(e) => setDeliveryPincode(e.target.value)}
                  onKeyDown={preventNegative}
                  className={`w-full border rounded-xl px-5 py-3 text-gray-800 placeholder-indigo-300 focus:outline-none focus:ring-4 transition ${
                    errors.deliveryPincode
                      ? "border-red-500 focus:ring-red-300"
                      : "border-indigo-300 focus:ring-indigo-300"
                  }`}
                  placeholder="Enter delivery pincode"
                />
                {errors.deliveryPincode && (
                  <p className="text-blue-500 text-sm mt-1">
                    {errors.deliveryPincode}
                  </p>
                )}
              </div>
            </div>

            {/* Toggle & Package Type */}
            <div className="flex items-center justify-between gap-8">
              {/* B2B/B2C Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-gray-600 font-medium">B2B</span>
                <button
                  type="button"
                  onClick={() => setPackageType(isB2B ? "B2C" : "B2B")}
                  className="bg-gray-300 relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                >
                  <span
                    className={`${
                      isB2B ? "translate-x-1" : "translate-x-6"
                    } inline-block h-4 w-4 transform rounded-full bg-indigo-600 transition`}
                  />
                </button>
                <span className="text-gray-600 font-medium">B2C</span>
              </div>

              {/* Advanced/Basic Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-gray-600 font-medium">Advanced</span>
                <button
                  type="button"
                  onClick={() => setShowAdvanced((prev) => !prev)}
                  className="bg-gray-300 relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                >
                  <span
                    className={`${
                      !showAdvanced ? "translate-x-6" : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-indigo-600 transition`}
                  />
                </button>
                <span className="text-gray-600 font-medium">Basic</span>
              </div>
            </div>

            {/* Box Inputs */}
            {showAdvanced ? (
              <div className="mt-6 space-y-6">
                <h3 className="text-lg font-semibold text-gray-700">
                  Advanced Package - Multiple Boxes
                </h3>
                {boxes.map((box, index) => (
                  <BoxInputs
                    key={index}
                    box={box}
                    index={index}
                    errors={errors.boxes[index]}
                    showAdvanced={showAdvanced}
                    handleBoxChange={handleBoxChange}
                    addBox={addBox}
                    deleteBox={deleteBox}
                    boxesLength={boxes.length}
                  />
                ))}
              </div>
            ) : (
              <div className="flex grid-cols-1 md:grid-cols-2 gap-8 items-center mt-6">
                {/* Weight */}
                <div className="flex flex-col">
                  <label className="text-gray-700 font-medium mb-2">
                    Weight (Kg)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="weight"
                    value={boxes[0]?.Weightperbox || ""}
                    onChange={(e) =>
                      handleBoxChange(0, "Weightperbox", e.target.value)
                    }
                    onKeyDown={preventNegative}
                    className={`w-full border rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:ring-4 transition ${
                      errors.boxes[0]?.Weightperbox
                        ? "border-red-500 focus:ring-red-300"
                        : "border-indigo-300 focus:ring-indigo-300"
                    }`}
                  />
                  <div className="min-h-[20px] mt-1">
                    {errors.boxes[0]?.Weightperbox && (
                      <p className="text-blue-500 text-sm">
                        {errors.boxes[0].Weightperbox}
                      </p>
                    )}
                  </div>
                </div>
                {/* Dimensions */}
                <div className="flex flex-col">
                  <label className="text-gray-700 font-medium mb-2">
                    Dimensions (L × B × H in cm)
                  </label>
                  <div className="flex gap-4">
                    {["length", "width", "height"].map((dim) => (
                      <div className="w-1/3" key={dim}>
                        <input
                          type="number"
                          min="0"
                          placeholder={dim.charAt(0).toUpperCase()}
                          value={boxes[0]?.[dim] || ""}
                          onChange={(e) =>
                            handleBoxChange(0, dim, e.target.value)
                          }
                          onKeyDown={preventNegative}
                          className={`w-full border rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:ring-4 transition appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                            errors.boxes[0]?.[dim]
                              ? "border-red-500 focus:ring-red-300"
                              : "border-indigo-300 focus:ring-indigo-300"
                          }`}
                        />
                        <div className="min-h-[18px] mt-1">
                          {errors.boxes[0]?.[dim] && (
                            <p className="text-blue-500 text-xs">
                              {errors.boxes[0][dim]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Payment Mode */}
            <div className="border border-indigo-300 rounded-2xl p-5 md:p-6 space-y-2 bg-white">
              <label className="block text-gray-700 font-semibold">
                Payment Mode
              </label>
              <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8 gap-4">
                <label className="flex items-center gap-2 text-indigo-700 font-medium">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMode === "Prepaid"}
                    onChange={() => setPaymentMode("Prepaid")}
                    className="w-5 h-5 accent-indigo-600 cursor-pointer"
                  />
                  Prepaid
                </label>
                <div className="flex flex-col lg:flex-row lg:items-center lg:gap-4 w-full">
                  <label className="flex items-center gap-2 text-indigo-700 font-medium">
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMode === "COD"}
                      onChange={() => setPaymentMode("COD")}
                      className="w-5 h-5 accent-indigo-600 cursor-pointer"
                    />
                    COD
                  </label>
                  {paymentMode === "COD" && (
                    <div className="w-full lg:max-w-xs">
                      {/* <label className=" text-sm font-medium text-gray-700 mb-1">
                        Amount to be Collected
                      </label> */}
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          ₹
                        </span>
                        <input
                          type="number"
                          min="0"
                          placeholder="COD amount"
                          value={codAmount}
                          onChange={(e) => setCodAmount(e.target.value)}
                          onKeyDown={preventNegative}
                          className={`w-full border rounded-xl pl-8 pr-3 py-2 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition ${
                            errors.codAmount
                              ? "border-red-500 focus:ring-red-400"
                              : "border-indigo-300 focus:ring-indigo-400"
                          }`}
                        />
                      </div>
                      {errors.codAmount && (
                        <p className="text-blue-500 text-sm mt-1">
                          {errors.codAmount}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Insurance */}
            {packageType === "B2B" && (
              <div className="border border-indigo-300 rounded-2xl p-5 md:p-6 space-y-2 bg-white relative">
                <label className="block text-gray-700 font-semibold">
                  Insurance
                </label>
                <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8 gap-4">
                  <label className="flex items-center gap-2 text-indigo-700 font-medium">
                    <input
                      type="radio"
                      name="insurance"
                      checked={insuranceOption === "ownerRisk"}
                      onChange={() => setInsuranceOption("ownerRisk")}
                      className="w-5 h-5 accent-indigo-600 cursor-pointer "
                    />
                    Owner's Risk
                  </label>
                  <label className="flex items-center gap-2 text-indigo-700 font-medium">
                    <input
                      type="radio"
                      name="insurance"
                      checked={insuranceOption === "carrierRisk"}
                      onChange={() => setInsuranceOption("carrierRisk")}
                      className="w-5 h-5 accent-indigo-600 cursor-pointer"
                    />
                    Carrier's Risk
                  </label>
                  {insuranceOption === "carrierRisk" && (
                    <div className="w-full lg:max-w-xs">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice Value
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                          ₹
                        </span>
                        <input
                          type="number"
                          min="0"
                          placeholder="Enter invoice value"
                          value={invoiceValue}
                          onChange={(e) => setInvoiceValue(e.target.value)}
                          onKeyDown={preventNegative}
                          className={`w-full border rounded-xl pl-8 pr-3 py-2 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition ${
                            errors.invoiceValue
                              ? "border-red-500 focus:ring-red-400"
                              : "border-indigo-300 focus:ring-indigo-400"
                          }`}
                        />
                      </div>
                      {errors.invoiceValue && (
                        <p className="text-blue-500 text-sm mt-1">
                          {errors.invoiceValue}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Self-Drop */}
            {packageType === "B2B" && (
              <div className="border border-indigo-300 rounded-2xl p-5 md:p-6 space-y-2 bg-white">
                <label className="block text-gray-700 font-semibold mb-2">
                  Do you want to self-drop your shipments at our center?
                </label>
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-8 gap-2">
                  <label className="flex items-center gap-2 text-indigo-700 font-medium">
                    <input
                      type="radio"
                      name="selfdrop"
                      checked={selfDropOption === "Pickup"}
                      onChange={() => setSelfDropOption("Pickup")}
                      className="w-5 h-5 accent-indigo-600 cursor-pointer"
                    />
                    No, Pickup from me
                  </label>
                  <label className="flex items-center gap-2 text-indigo-700 font-medium">
                    <input
                      type="radio"
                      name="selfdrop"
                      checked={selfDropOption === "Self-drop"}
                      onChange={() => setSelfDropOption("Self-drop")}
                      className="w-5 h-5 accent-indigo-600 cursor-pointer"
                    />
                    Yes, I'll self-drop
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={onCalculate}
          className="bg-blue-900 hover:bg-blue-800 text-white font-bold py-2 px-8 rounded-xl shadow-lg transition duration-300 text-lg cursor-pointer"
        >
          Calculate Rate
        </button>
      </div>
    </div>
  );
}
